import { useMemo } from 'react';
import { useTranslation } from '@eon/i18n';
import {
  Badge,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  type Column,
} from '@eon/ui';
import {
  hooks,
  type AllowanceHistoryEntry,
  type OmSessionListItem,
  type OmUser,
} from '@eon/api-client';
import { cn, formatDateTime, formatKWh } from '@eon/utils';
import { useClientTableSort } from '@/lib/table-sort';
import { AllowanceBalance } from '@/components/AllowanceBalance';

interface AllowanceHistoryDialogProps {
  user: OmUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ActivityKind = 'load' | 'charge' | 'refund' | 'decrease' | 'correction' | 'other';

interface ActivityRow {
  id: string;
  at: string;
  kind: ActivityKind;
  deltaKwh: number;
  detail: string;
}

function classifyReason(reason: string | undefined, deltaKwh: number): ActivityKind {
  const r = (reason ?? '').toLowerCase();
  if (r.startsWith('refund:')) return 'refund';
  if (r.startsWith('charge:') || r.startsWith('charge:session')) return 'charge';
  if (r.startsWith('migration_reconciliation') || r.includes('reconcil') || r.includes('correction')) {
    return 'correction';
  }
  if (r === 'increase' || r === 'load' || r === 'topup' || r === 'top_up') return 'load';
  if (r === 'decrease') return 'decrease';
  if (deltaKwh > 0) return 'load';
  if (deltaKwh < 0) return 'decrease';
  return 'other';
}

function sessionIdFromReason(reason?: string): string | undefined {
  const match = reason?.match(/^(?:charge|refund):session:(.+)$/i);
  return match?.[1];
}

function sessionLocation(session: Pick<OmSessionListItem, 'stationName' | 'deviceDisplayName'>): string {
  return [session.stationName, session.deviceDisplayName].filter(Boolean).join(' · ');
}

function buildActivity(
  history: AllowanceHistoryEntry[] | undefined,
  sessions: OmSessionListItem[] | undefined,
  labels: {
    load: string;
    decrease: string;
    charge: string;
    refund: string;
    correction: string;
  },
): ActivityRow[] {
  const sessionById = new Map((sessions ?? []).map((s) => [s.id, s]));
  const coveredSessionIds = new Set<string>();
  const rows: ActivityRow[] = [];

  for (const entry of history ?? []) {
    const kind = classifyReason(entry.reason, entry.deltaKwh);
    const sid = sessionIdFromReason(entry.reason);
    if (sid) coveredSessionIds.add(sid);
    const session = sid ? sessionById.get(sid) : undefined;
    const location = session ? sessionLocation(session) : '';

    let detail = labels.charge;
    if (kind === 'load') detail = labels.load;
    else if (kind === 'decrease') detail = labels.decrease;
    else if (kind === 'correction') detail = labels.correction;
    else if (kind === 'refund') detail = location ? `${labels.refund} · ${location}` : labels.refund;
    else if (kind === 'charge') detail = location || labels.charge;

    rows.push({
      id: entry.id,
      at: entry.createdAt,
      kind,
      deltaKwh: entry.deltaKwh,
      detail,
    });
  }

  for (const session of sessions ?? []) {
    if (coveredSessionIds.has(session.id)) continue;
    rows.push({
      id: `session:${session.id}`,
      at: session.startedAt,
      kind: 'charge',
      deltaKwh: -(session.totalEnergyKWh ?? 0),
      detail: sessionLocation(session) || labels.charge,
    });
  }

  rows.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  return rows;
}

const KIND_VARIANT: Record<ActivityKind, 'success' | 'info' | 'warning' | 'secondary' | 'outline'> = {
  load: 'success',
  charge: 'info',
  refund: 'success',
  decrease: 'warning',
  correction: 'secondary',
  other: 'outline',
};

export function AllowanceHistoryDialog({ user, open, onOpenChange }: AllowanceHistoryDialogProps) {
  const { t } = useTranslation();
  const userId = open && user ? user.userId : '';
  const history = hooks.useOmAllowanceHistory(userId);
  const sessions = hooks.useMySessions({
    userId,
    page: 1,
    pageSize: 200,
    enabled: Boolean(userId),
  });

  const activity = useMemo(
    () =>
      buildActivity(history.data, sessions.data?.data, {
        load: t('portal.users.activityReasonLoad'),
        decrease: t('portal.users.activityReasonDecrease'),
        charge: t('portal.users.activityReasonCharge'),
        refund: t('portal.users.activityReasonRefund'),
        correction: t('portal.users.activityReasonCorrection'),
      }),
    [history.data, sessions.data?.data, t],
  );

  const kindLabel = (kind: ActivityKind) => {
    if (kind === 'load') return t('portal.users.activityLoad');
    if (kind === 'charge') return t('portal.users.activityCharge');
    if (kind === 'refund') return t('portal.users.activityRefund');
    if (kind === 'decrease') return t('portal.users.activityDecrease');
    if (kind === 'correction') return t('portal.users.activityCorrection');
    return t('portal.users.activityOther');
  };

  const columns: Column<ActivityRow>[] = [
    {
      key: 'at',
      header: t('logs.col.time'),
      sortable: true,
      sortValue: (row) => row.at,
      cell: (row) => formatDateTime(row.at),
    },
    {
      key: 'kind',
      header: t('portal.users.activityType'),
      sortable: true,
      sortValue: (row) => kindLabel(row.kind),
      cell: (row) => <Badge variant={KIND_VARIANT[row.kind]}>{kindLabel(row.kind)}</Badge>,
    },
    {
      key: 'delta',
      header: t('portal.users.allowanceDelta'),
      sortable: true,
      sortValue: (row) => row.deltaKwh,
      cell: (row) => (
        <span
          className={cn(
            'font-medium tabular-nums',
            row.deltaKwh < 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400',
          )}
        >
          {row.deltaKwh > 0 ? '+' : ''}
          {formatKWh(row.deltaKwh)}
        </span>
      ),
    },
    {
      key: 'detail',
      header: t('portal.users.activityDetail'),
      sortable: true,
      sortValue: (row) => row.detail,
      cell: (row) => <span className="text-muted-foreground text-sm">{row.detail}</span>,
    },
  ];

  const { sort, onSortChange, sortedData } = useClientTableSort(activity, columns);
  const loading = history.isLoading || sessions.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('portal.users.allowanceHistoryTitle')}</DialogTitle>
        </DialogHeader>
        {user ? (
          <div className="space-y-3">
            <p className="text-muted-foreground -mt-1 text-sm">{user.name ?? user.email}</p>
            <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
              <span className="text-muted-foreground text-sm">{t('portal.users.allowanceCurrent')}</span>
              <AllowanceBalance value={user.remainingAllowance ?? user.allowanceKwh} />
            </div>
          </div>
        ) : null}
        <DataTable
          data={sortedData}
          columns={columns}
          loading={loading}
          rowKey={(row) => row.id}
          emptyTitle={t('portal.users.noAllowanceHistory')}
          sort={sort}
          onSortChange={onSortChange}
        />
      </DialogContent>
    </Dialog>
  );
}
